import * as THREE from "three";

/**
 * EyeTrackingController (AUTO EYE MESH DETECTION)
 *
 * Keeps EXACT tracking logic.
 *
 * Key upgrade:
 * - Auto-detects which meshes should receive eyeLook* weights.
 * - Works across models with different mesh names/prefixes.
 * - If detection fails, falls back to applying to all targets (so nothing breaks).
 *
 * Options:
 * - opts.autoDetectEyeMeshes (default true)
 * - opts.minEyeLookKeysForMesh (default 6)   // how many eyeLook keys must exist on a mesh to be considered
 * - opts.eyeIndexMax (default 25)            // prefer meshes whose eyeLook indices are <= this
 * - opts.forceEyeMeshes = ["Mesh012","Mesh012_1"] // optional manual override
 * - opts.filterEyeLookToEyeMeshes (default true)
 * - opts.filterEyeBlinkToEyeMeshes (default false)
 */

export class EyeTrackingController {
    constructor(blendShapeControls, camera, model, opts = {}) {
        this.blendShapeControls = blendShapeControls || {};
        this.camera = camera;
        this.model = model;
        this.enabled = true;

        // --- Config ---
        this.eps = opts.eps ?? 0.001;
        // World-space height of the character's eyes above the model's
        // origin, in scene units. Pass the value detected by
        // CharacterPoseConfig.detectEyeHeight() — the legacy default of
        // 1.65 was meters and produced near-zero offsets in this scene's
        // centimeter world (eyes ended up at the model's feet).
        this.eyeLevel = Number.isFinite(opts.eyeLevel) && opts.eyeLevel > 0
            ? opts.eyeLevel
            : 165;
        this.debug = opts.debug ?? false;

        this.autoDetectEyeMeshes = opts.autoDetectEyeMeshes !== false; // default true
        this.minEyeLookKeysForMesh = opts.minEyeLookKeysForMesh ?? 6;
        this.eyeIndexMax = opts.eyeIndexMax ?? 25;

        this.filterEyeLookToEyeMeshes = opts.filterEyeLookToEyeMeshes !== false; // default true
        this.filterEyeBlinkToEyeMeshes =
            opts.filterEyeBlinkToEyeMeshes === true; // default false

        // Track camera position/rotation
        this.lastCameraPosition = this.camera
            ? this.camera.getWorldPosition(new THREE.Vector3())
            : null;
        this.lastCameraRotation = this.camera
            ? this.camera.rotation.clone()
            : null;

        // Candidates
        this.eyeBlendShapeCandidates = {
            left: {
                lookUp: [
                    "BlendShape_EyeL.EyeLookUp_L",
                    "Eye_L.Eye_L_lookUp",
                    "Mesh012.eyeLookUpLeft",
                    "eyeLookUpLeft",
                ],
                lookDown: [
                    "BlendShape_EyeL.EyeLookDown_L",
                    "Eye_L.Eye_L_lookDown",
                    "Mesh012.eyeLookDownLeft",
                    "eyeLookDownLeft",
                ],
                lookIn: [
                    "BlendShape_EyeL.EyeLookIn_L",
                    "Eye_L.Eye_L_lookRight",
                    "Mesh012.eyeLookInLeft",
                    "eyeLookInLeft",
                ],
                lookOut: [
                    "BlendShape_EyeL.EyeLookOut_L",
                    "Eye_L.Eye_L_lookLeft",
                    "Mesh012.eyeLookOutLeft",
                    "eyeLookOutLeft",
                ],
            },
            right: {
                lookUp: [
                    "BlendShape_EyeR.EyeLookUp_R",
                    "Eye_R.Eye_R_lookUp",
                    "Mesh012.eyeLookUpRight",
                    "eyeLookUpRight",
                ],
                lookDown: [
                    "BlendShape_EyeR.EyeLookDown_R",
                    "Eye_R.Eye_R_lookDown",
                    "Mesh012.eyeLookDownRight",
                    "eyeLookDownRight",
                ],
                lookIn: [
                    "BlendShape_EyeR.EyeLookIn_R",
                    "Eye_R.Eye_R_lookLeft",
                    "Mesh012.eyeLookInRight",
                    "eyeLookInRight",
                ],
                lookOut: [
                    "BlendShape_EyeR.EyeLookOut_R",
                    "Eye_R.Eye_R_lookRight",
                    "Mesh012.eyeLookOutRight",
                    "eyeLookOutRight",
                ],
            },
        };

        // Resolve keys (still suffix-friendly)
        this.eyeBlendShapes = this.resolveBlendShapes();

        const totalFound =
            Object.values(this.eyeBlendShapes.left).filter(
                (k) => !!this.blendShapeControls[k],
            ).length +
            Object.values(this.eyeBlendShapes.right).filter(
                (k) => !!this.blendShapeControls[k],
            ).length;

        if (totalFound === 0) {
            console.warn("[EYE] detect zero blendshapes for eye tracking");
            this.enabled = false;
            this.setupUI();
            return;
        }

        // ✅ Eye mesh allowlist
        this._eyeMeshAllow = null;

        // Manual override
        if (
            Array.isArray(opts.forceEyeMeshes) &&
            opts.forceEyeMeshes.length > 0
        ) {
            this._eyeMeshAllow = new Set(opts.forceEyeMeshes.map(String));
            console.log(
                "[EYE] Using FORCE eye meshes:",
                Array.from(this._eyeMeshAllow),
            );
        } else if (this.autoDetectEyeMeshes) {
            this._eyeMeshAllow = this._detectEyeMeshesFromControls();
            if (this._eyeMeshAllow && this._eyeMeshAllow.size > 0) {
                console.log(
                    "[EYE] Auto-detected eye meshes:",
                    Array.from(this._eyeMeshAllow),
                );
            } else {
                this._eyeMeshAllow = null; // means no filtering (fallback)
                console.warn(
                    "[EYE] Auto-detect failed; will NOT filter eyeLook targets (fallback).",
                );
            }
        }

        console.log(`[EYE] Eye tracking shapes detected: ${totalFound}/8`);
        this._logResolvedEyeShapesFull();

        this.setupUI();
    }

    _suffixOf(name) {
        const s = String(name || "");
        const dot = s.lastIndexOf(".");
        return dot !== -1 ? s.slice(dot + 1) : s;
    }

    _findControlKey(candidate) {
        if (!candidate) return null;

        if (this.blendShapeControls[candidate]) return candidate;

        const suf = this._suffixOf(candidate);
        if (this.blendShapeControls[suf]) return suf;

        const candLow = String(candidate).toLowerCase();
        for (const k of Object.keys(this.blendShapeControls)) {
            if (k.toLowerCase() === candLow) return k;
        }

        const sufLow = String(suf).toLowerCase();
        for (const k of Object.keys(this.blendShapeControls)) {
            if (k.toLowerCase() === sufLow) return k;
        }

        return null;
    }

    resolveBlendShapes() {
        const resolved = { left: {}, right: {} };

        for (const eye of ["left", "right"]) {
            for (const dir of Object.keys(this.eyeBlendShapeCandidates[eye])) {
                const candidates = this.eyeBlendShapeCandidates[eye][dir] || [];
                let found = null;

                for (const name of candidates) {
                    const key = this._findControlKey(name);
                    if (key) {
                        found = key;
                        break;
                    }
                }

                const fallback = candidates[0]
                    ? this._suffixOf(candidates[0])
                    : null;
                resolved[eye][dir] = found || fallback || null;
            }
        }

        return resolved;
    }

    _getEntry(key) {
        const e = this.blendShapeControls?.[key];
        if (!e) return null;
        return Array.isArray(e) ? e : [e];
    }

    // ✅ Detect which meshes are the real eye meshes by analyzing eyeLook* indices
    _detectEyeMeshesFromControls() {
        const lookKeys = [
            "eyeLookUpLeft",
            "eyeLookDownLeft",
            "eyeLookInLeft",
            "eyeLookOutLeft",
            "eyeLookUpRight",
            "eyeLookDownRight",
            "eyeLookInRight",
            "eyeLookOutRight",
        ];

        // Map: meshName -> { count, indices[] }
        const stats = new Map();

        for (const lk of lookKeys) {
            const entry = this.blendShapeControls[lk];
            if (!entry) continue;
            const ctls = Array.isArray(entry) ? entry : [entry];

            for (const ctl of ctls) {
                const meshName = ctl?.mesh?.name;
                const idx = ctl?.index;
                if (!meshName || typeof idx !== "number") continue;

                if (!stats.has(meshName))
                    stats.set(meshName, { count: 0, indices: [] });
                const s = stats.get(meshName);
                s.count++;
                s.indices.push(idx);
            }
        }

        if (stats.size === 0) return null;

        // Score meshes: prefer
        // - high count (many look keys present)
        // - low indices (<= eyeIndexMax)
        // - tight index range
        const scored = [];
        for (const [meshName, s] of stats.entries()) {
            const count = s.count;
            const indices = s.indices.slice().sort((a, b) => a - b);
            const min = indices[0];
            const max = indices[indices.length - 1];
            const range = max - min;

            const lowCount = indices.filter(
                (x) => x <= this.eyeIndexMax,
            ).length;

            // Hard filters
            if (count < this.minEyeLookKeysForMesh) continue;

            // Score formula (simple + robust)
            const score =
                count * 10 +
                lowCount * 5 +
                (this.eyeIndexMax - Math.min(this.eyeIndexMax, min)) +
                Math.max(0, 20 - range);

            scored.push({ meshName, count, lowCount, min, max, range, score });
        }

        if (scored.length === 0) return null;

        scored.sort((a, b) => b.score - a.score);

        if (this.debug) {
            console.log("[EYE] Eye mesh detection scores:", scored);
        }

        // Take the top 2 meshes (usually left/right eyeballs)
        const top = scored.slice(0, 2).map((x) => x.meshName);
        return new Set(top);
    }

    _getFullMorphKeysForTarget(mesh, ctlIndex, preferredSuffix) {
        const dict = mesh?.morphTargetDictionary;
        if (!dict) return [];
        const keys = Object.keys(dict);

        const out = [];
        const prefSufLow = String(preferredSuffix || "").toLowerCase();

        for (const k of keys) if (dict[k] === ctlIndex) out.push(k);
        if (out.length > 0) return out;

        for (const k of keys) {
            const suf = this._suffixOf(k).toLowerCase();
            if (suf === prefSufLow && dict[k] === ctlIndex) out.push(k);
        }
        if (out.length > 0) return out;

        for (const k of keys) {
            const suf = this._suffixOf(k).toLowerCase();
            if (suf === prefSufLow) out.push(k);
        }
        return out;
    }

    _logResolvedEyeShapesFull() {
        const entries = [];
        const add = (eye, dir, key) => {
            const exists = !!this.blendShapeControls?.[key];
            const candidates = this.eyeBlendShapeCandidates?.[eye]?.[dir] || [];
            entries.push({ eye, dir, key, exists, candidates });
        };

        add("left", "lookUp", this.eyeBlendShapes.left.lookUp);
        add("left", "lookDown", this.eyeBlendShapes.left.lookDown);
        add("left", "lookIn", this.eyeBlendShapes.left.lookIn);
        add("left", "lookOut", this.eyeBlendShapes.left.lookOut);

        add("right", "lookUp", this.eyeBlendShapes.right.lookUp);
        add("right", "lookDown", this.eyeBlendShapes.right.lookDown);
        add("right", "lookIn", this.eyeBlendShapes.right.lookIn);
        add("right", "lookOut", this.eyeBlendShapes.right.lookOut);

        console.groupCollapsed(
            `%c[EYE] FULL resolved eye shapes + mesh + FULL morph keys (${entries.length})`,
            "color:#00ccff;font-weight:bold",
        );

        console.log(
            "%c[EYE] EyeLook filter meshes:",
            "color:#ffaa00;font-weight:bold",
            this._eyeMeshAllow
                ? Array.from(this._eyeMeshAllow)
                : "(no filter / fallback)",
        );

        for (const e of entries) {
            const tag = `${e.eye.toUpperCase()} ${e.dir}`;
            const status = e.exists ? "✅ FOUND" : "❌ NOT FOUND";
            console.groupCollapsed(
                `[EYE] ${tag}: ${status}  controlKey="${e.key}"`,
            );

            console.log("[EYE] candidates:");
            for (const c of e.candidates) console.log("  " + c);

            const ctls = e.key ? this._getEntry(e.key) : null;
            if (!ctls) {
                console.warn("[EYE] No control entry for key:", e.key);
                console.groupEnd();
                continue;
            }

            const suffix = this._suffixOf(e.key).toLowerCase();
            const isLook = suffix.startsWith("eyelook");

            console.log(`[EYE] targets: ${ctls.length}`);
            for (let i = 0; i < ctls.length; i++) {
                const ctl = ctls[i];
                const mesh = ctl?.mesh;
                const idx = typeof ctl?.index === "number" ? ctl.index : null;

                const fullKeys = this._getFullMorphKeysForTarget(
                    mesh,
                    idx,
                    this._suffixOf(e.key),
                );

                const allowed =
                    !this.filterEyeLookToEyeMeshes ||
                    !isLook ||
                    !this._eyeMeshAllow ||
                    this._eyeMeshAllow.has(mesh?.name);

                console.log(
                    `[EYE] target[${i}] mesh="${mesh?.name ?? "NO_MESH"}" ctlIndex=${idx} allowed=${allowed}`,
                );

                if (fullKeys.length > 0) {
                    console.log("[EYE]   FULL morph keys:");
                    for (const fk of fullKeys) console.log("      " + fk);
                }
            }

            console.groupEnd();
        }

        console.groupEnd();
    }

    setupUI() {
        const debugPanel = document.getElementById("debug-panel");
        if (!debugPanel) return;

        const eyeTrackingControls = document.createElement("div");
        eyeTrackingControls.className = "mt-3";
        eyeTrackingControls.innerHTML = `
      <small class="text-muted">Eye Tracking Controls:</small>
      <div class="mb-2">
        <label class="form-check form-switch">
          <input class="form-check-input" type="checkbox" id="eye-tracking-toggle" checked>
          <span class="form-check-label">Enable Eye Tracking</span>
        </label>
      </div>
    `;
        debugPanel.appendChild(eyeTrackingControls);

        const toggle = document.getElementById("eye-tracking-toggle");
        if (toggle) {
            toggle.addEventListener("change", (e) => {
                this.enabled = e.target.checked;
                if (!this.enabled) this.resetEyeRotation();
            });
        }
    }

    update(time) {
        if (!this.enabled || !this.camera || !this.model) return;

        if (this.hasCameraChanged()) {
            if (this.debug)
                console.log(
                    "[EYE] Camera moved/rotated - updating eye tracking",
                );
            this.updateEyeRotation();

            this.lastCameraPosition = this.camera.getWorldPosition(
                new THREE.Vector3(),
            );
            this.lastCameraRotation = this.camera.rotation.clone();
        }
    }

    hasCameraChanged() {
        if (!this.lastCameraPosition || !this.lastCameraRotation) return true;

        const camPos = this.camera.getWorldPosition(new THREE.Vector3());

        const positionChanged =
            Math.abs(camPos.x - this.lastCameraPosition.x) > this.eps ||
            Math.abs(camPos.y - this.lastCameraPosition.y) > this.eps ||
            Math.abs(camPos.z - this.lastCameraPosition.z) > this.eps;

        const rotationChanged =
            Math.abs(this.camera.rotation.x - this.lastCameraRotation.x) >
                this.eps ||
            Math.abs(this.camera.rotation.y - this.lastCameraRotation.y) >
                this.eps ||
            Math.abs(this.camera.rotation.z - this.lastCameraRotation.z) >
                this.eps;

        return positionChanged || rotationChanged;
    }

    updateEyeRotation() {
        if (!this.camera || !this.model) return;

        this.camera.updateMatrixWorld(true);
        this.model.updateMatrixWorld(true);

        const modelWorld = this.model.getWorldPosition(new THREE.Vector3());
        const modelEyeWorld = modelWorld.clone();
        modelEyeWorld.y = modelWorld.y + this.eyeLevel;

        const cameraWorld = this.camera.getWorldPosition(new THREE.Vector3());
        const direction = cameraWorld.sub(modelEyeWorld);

        // Convert world-space gaze direction into the model's local frame
        // so the horizontal angle is measured relative to the character's
        // forward axis. Without this, characters with a non-zero
        // characterData[id].modelRotationY override would gaze off-axis.
        const modelQuat = this.model.getWorldQuaternion(new THREE.Quaternion());
        const localDir = direction.clone().applyQuaternion(modelQuat.invert());

        const horizontalAngle = Math.atan2(localDir.x, localDir.z);
        const verticalAngle = -this.camera.rotation.x;

        this.setEyeWeights(verticalAngle, horizontalAngle);
    }

    /**
     * Update eyeLevel from the world-space eye anchors detected by
     * EyeFraming.detectEyeAnchors(). Stores the eye height as the
     * vertical offset between the model's origin and the eye midpoint
     * in world units (centimeters in this scene), which matches the
     * convention used by the gaze math in detectCameraMovement().
     *
     * @param {{midpoint: THREE.Vector3}} anchors
     * @param {THREE.Object3D} model
     */
    setEyeLevelFromWorldAnchors(anchors, model) {
        if (!anchors || !anchors.midpoint || !model) return;
        try {
            const modelWorldPos = new THREE.Vector3();
            model.getWorldPosition(modelWorldPos);
            const eyeY = anchors.midpoint.y - modelWorldPos.y;
            if (Number.isFinite(eyeY) && eyeY > 0) {
                this.eyeLevel = eyeY;
            }
            // Also store the authoritative left/right/midpoint anchors
            // in model-local space so future gaze refinements can aim
            // each eye at the camera independently rather than rotating
            // off the cached scalar eyeLevel.
            if (anchors.leftEye && anchors.rightEye) {
                const modelInv = new THREE.Matrix4()
                    .copy(model.matrixWorld)
                    .invert();
                this.eyeAnchorsLocal = {
                    left: anchors.leftEye.clone().applyMatrix4(modelInv),
                    right: anchors.rightEye.clone().applyMatrix4(modelInv),
                    midpoint: anchors.midpoint.clone().applyMatrix4(modelInv),
                    eyeDistance: anchors.eyeDistance,
                    source: anchors.source,
                };
            }
        } catch (_e) {
            /* keep previous eyeLevel */
        }
    }

    setEyeWeights(verticalAngle, horizontalAngle) {
        const horizontalWeight = Math.max(
            -1,
            Math.min(1, horizontalAngle / (Math.PI / 3)),
        );
        // Clamp vertical eye motion to ±0.4 (≈±23°) so the pupils can
        // actually track the camera as the user orbits up/down. The old
        // ±0.1 cap pinned the eyes nearly straight ahead.
        const verticalWeight = Math.max(-0.4, Math.min(0.4, verticalAngle));

        this.setBlendShapeWeight(this.eyeBlendShapes.left.lookUp, 0);
        this.setBlendShapeWeight(this.eyeBlendShapes.right.lookUp, 0);
        this.setBlendShapeWeight(this.eyeBlendShapes.left.lookDown, 0);
        this.setBlendShapeWeight(this.eyeBlendShapes.right.lookDown, 0);

        if (verticalWeight > 0) {
            this.setBlendShapeWeight(
                this.eyeBlendShapes.left.lookUp,
                verticalWeight,
            );
            this.setBlendShapeWeight(
                this.eyeBlendShapes.right.lookUp,
                verticalWeight,
            );
        } else {
            this.setBlendShapeWeight(
                this.eyeBlendShapes.left.lookDown,
                -verticalWeight,
            );
            this.setBlendShapeWeight(
                this.eyeBlendShapes.right.lookDown,
                -verticalWeight,
            );
        }

        if (horizontalWeight > 0) {
            this.setBlendShapeWeight(
                this.eyeBlendShapes.left.lookOut,
                horizontalWeight,
            );
            this.setBlendShapeWeight(
                this.eyeBlendShapes.right.lookIn,
                horizontalWeight,
            );
            this.setBlendShapeWeight(this.eyeBlendShapes.left.lookIn, 0);
            this.setBlendShapeWeight(this.eyeBlendShapes.right.lookOut, 0);
        } else {
            this.setBlendShapeWeight(this.eyeBlendShapes.left.lookOut, 0);
            this.setBlendShapeWeight(this.eyeBlendShapes.right.lookIn, 0);
            this.setBlendShapeWeight(
                this.eyeBlendShapes.left.lookIn,
                -horizontalWeight,
            );
            this.setBlendShapeWeight(
                this.eyeBlendShapes.right.lookOut,
                -horizontalWeight,
            );
        }
    }

    setBlendShapeWeight(shapeKey, weight) {
        if (!shapeKey) return;

        let key = shapeKey;
        if (!this.blendShapeControls[key]) {
            const suf = this._suffixOf(key);
            if (this.blendShapeControls[suf]) key = suf;
        }

        const entry = this.blendShapeControls[key];
        if (!entry) return;

        const clamped = Math.max(0, Math.min(1, weight));
        const suffix = this._suffixOf(key).toLowerCase();
        const isLook = suffix.startsWith("eyelook");

        const shouldFilter =
            isLook &&
            this.filterEyeLookToEyeMeshes &&
            this._eyeMeshAllow &&
            this._eyeMeshAllow.size > 0;

        const apply = (ctl) => {
            if (!ctl || !ctl.mesh || typeof ctl.index !== "number") return;

            // ✅ Only filter if we successfully detected eye meshes
            if (shouldFilter && !this._eyeMeshAllow.has(ctl.mesh.name)) return;

            const inf = ctl.mesh.morphTargetInfluences;
            if (inf && inf.length > ctl.index) inf[ctl.index] = clamped;
        };

        if (Array.isArray(entry)) for (const c of entry) apply(c);
        else apply(entry);
    }

    resetEyeRotation() {
        Object.values(this.eyeBlendShapes.left).forEach((shape) =>
            this.setBlendShapeWeight(shape, 0),
        );
        Object.values(this.eyeBlendShapes.right).forEach((shape) =>
            this.setBlendShapeWeight(shape, 0),
        );
    }
}
